import { CssBaseline, ThemeProvider } from "@mui/material";
import { createTheme } from "@mui/material/styles";
import { lazy, Suspense, useMemo } from "react";
import ChunkErrorBoundary from "components/ChunkErrorBoundary";
import { useSelector } from "react-redux";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { themeSettings } from "theme";
import jwt_decode from "jwt-decode";
import { useEffect } from "react";
import { useLogout } from "state/hooks/useLogout";
import { usePushAutoResubscribe } from "state/hooks/usePushAutoResubscribe";
import { useIdentitySync } from "state/hooks/useIdentitySync";
import {
  isAdmin,
  isAccountManager,
  isFinancialManager,
  canViewFinancialDashboard,
  isMediaBuyer,
  isMediaBuyingHead,
  isMediaBuyingTeamLeader,
} from "utils/roles";
import Layout from "pages/Layout";
import Login from "pages/Login";
import SetInitialPassword from "pages/SetInitialPassword";
import GetApp from "pages/GetApp";
import { useSwMessages } from "pwa/useSwMessages";
import UpdatePrompt from "pwa/UpdatePrompt";
import { ConfirmProvider } from "components/ConfirmDialog";
import NotFound from "components/NotFound";
import LoadingGate from "components/LoadingGate";
import { ToastProvider } from "components/Toast";
import Unsubscribe from "pages/Unsubscribe";
import NativeEvents from "native/NativeEvents";
import NativeUpdateGate from "native/NativeUpdateGate";
import MoveToAppStoreGate from "pwa/MoveToAppStoreGate";

// Role redirect that keeps the query string, so a shared /leads?... deep
// link (dashboard numbers, "Awaiting feedback") lands on the role's own list
// with the same filters. A string `to` on <Navigate> drops the search.
function RedirectWithSearch({ to }) {
  const { search } = useLocation();
  return <Navigate to={{ pathname: to, search }} replace />;
}

// Route-level code splitting: every page below is a lazy chunk so a phone
// only downloads the modules its role actually opens (recharts, the
// DataGrid, the Media Buying + Financial modules all leave the main
// bundle). The shell (Layout/Navbar/BottomNav) and the logged-out entry
// (Login / SetInitialPassword) stay eager; Layout owns the Suspense
// boundary around its <Outlet/> so the chrome never blinks while a page
// chunk loads.
const Dashboard = lazy(() => import("pages/dashboard/Dashboard"));
const Projects = lazy(() => import("pages/projects/Projects"));
const CreateProject = lazy(() => import("pages/projects/CreateProject"));
const SingleProject = lazy(() => import("pages/projects/SingleProject"));
const UpdateProject = lazy(() => import("pages/projects/UpdateProject"));
const Leads = lazy(() => import("pages/leads/Leads"));
const SingleLead = lazy(() => import("pages/leads/SingleLead"));
const SyncHistory = lazy(() => import("pages/leads/SyncHistory"));
const Clients = lazy(() => import("pages/clients/Clients"));
const CreateClient = lazy(() => import("pages/clients/CreateClient"));
const SingleClient = lazy(() => import("pages/clients/SingleClient"));
const ForgotPassword = lazy(() => import("pages/ForgotPassword"));
const ResetPassword = lazy(() => import("pages/ResetPassword"));
const UpdateClient = lazy(() => import("pages/clients/UpdateClient"));
const Settings = lazy(() => import("pages/settings/Settings"));
const Team = lazy(() => import("pages/team/Team"));
const CrmDelivery = lazy(() => import("pages/crmDelivery/CrmDelivery"));
const Broadcast = lazy(() => import("pages/broadcast/Broadcast"));
const NewsletterUnsubscribe = lazy(() =>
  import("pages/newsletter/NewsletterUnsubscribe")
);
const Notifications = lazy(() => import("pages/notifications/Notifications"));
const ClientDashboard = lazy(() => import("pages/clientPages/ClientDashboard"));
const ClientLeads = lazy(() => import("pages/clientPages/ClientLeads"));
const ClientSettings = lazy(() => import("pages/clientPages/settings/ClientSettings"));
const ClientTeamMembers = lazy(() => import("pages/clientPages/teamMembers/ClientTeamMembers"));
const SingleTeamMember = lazy(() => import("pages/clientPages/teamMembers/SingleTeamMember"));
const TeamMemberDashboard = lazy(() => import("pages/teamMemberPages/TeamMemberDashboard"));
const TeamMemberLeads = lazy(() => import("pages/teamMemberPages/TeamMemberLeads"));
const TeamMemberSettings = lazy(() => import("pages/teamMemberPages/TeamMemberSettings"));
const CreateTeamMember = lazy(() => import("pages/clientPages/teamMembers/CreateTeamMember"));
const SuperClientDashboard = lazy(() => import("pages/superClientPages/SuperClientDashboard"));
const SuperClientLeads = lazy(() => import("pages/superClientPages/SuperClientLeads"));
const SuperClientSettings = lazy(() => import("pages/superClientPages/SuperClientSettings"));
const AccountManagerDashboard = lazy(() => import("pages/accountManager/AccountManagerDashboard"));
const MyLeads = lazy(() => import("pages/accountManager/MyLeads"));
const AmProjects = lazy(() => import("pages/accountManager/AmProjects"));
const SingleMyLead = lazy(() => import("pages/accountManager/SingleMyLead"));
const MediaBuyingHome = lazy(() => import("pages/mediaBuying/MediaBuyingHome"));
const NewClientHome = lazy(() => import("pages/mediaBuying/newClient/NewClientHome"));
const HeadFinancials = lazy(() => import("pages/financialManager/HeadFinancials"));
const FinancialDashboard = lazy(() => import("pages/financialManager/FinancialDashboard"));
const UnmatchedCampaigns = lazy(() => import("pages/financialManager/UnmatchedCampaigns"));
const ClientPricing = lazy(() => import("pages/financialManager/ClientPricing"));
const Mappings = lazy(() => import("pages/financialManager/Mappings"));
const Partners = lazy(() => import("pages/financialManager/Partners"));
const Invoices = lazy(() => import("pages/financialManager/Invoices"));
const BudgetTracker = lazy(() => import("pages/financialManager/BudgetTracker"));
const ClientFinancialDetail = lazy(() => import("pages/financialManager/ClientFinancialDetail"));
const ActivityAudit = lazy(() => import("pages/financialManager/ActivityAudit"));
const FMClients = lazy(() => import("pages/financialManager/FMClients"));

// Legacy /projects/* URLs are now /prefixes/*. Anyone hitting an old
// bookmark gets transparently redirected to the new path, preserving the
// rest of the URL (sub-route, query string, hash).
function LegacyProjectsRedirect() {
  const location = useLocation();
  const newPath = location.pathname.replace(/^\/projects/, "/prefixes");
  return (
    <Navigate
      to={`${newPath}${location.search}${location.hash}`}
      replace
    />
  );
}

function App() {
  // useSelector is provided by react-redux and allows us to access the global state
  const mode = useSelector((state) => state.global.mode);
  // useMemo is used to optimize expensive computations by not doing them again unless the [mode] value changes
  // createTheme customizes the Material UI theme based on the themeSettings we provide
  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);

  const user = useSelector((state) => state.auth.user);
  const impersonator = useSelector((state) => state.auth.impersonator);
  // During impersonation, the target's bootstrap flag does not block the
  // admin — they retain full role-specific routing. The set-password screen
  // is still reachable by URL if the admin wants to help the client pick one.
  const gatedToSetPassword =
    !!user?.mustSetPassword && !impersonator;

  const { logout } = useLogout();

  // Re-arm this device's push subscription after login (no-op unless the user
  // had already granted permission and not turned notifications off). Logout
  // tears the subscription down; this restores it without re-prompting.
  usePushAutoResubscribe();

  // Reconcile the stored login snapshot with what /me says this person's role
  // and permissions are now. Without it, a role change or a permission toggle
  // only reaches someone's sidebar at their next sign-in — Hana was made a
  // Team Leader and went on seeing a plain buyer's tabs.
  useIdentitySync();

  // Service-worker messages (a push landing while the app is open) refresh
  // the in-app notification badge event-driven — no polling.
  useSwMessages();

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user?.token) {
      const decodedToken = jwt_decode(user.token);
      const currentTime = Date.now() / 1000;

      if (decodedToken.exp < currentTime) {
        // Token has expired, redirect to login page
        logout();
      }
    }
  }, [logout]);

  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <ConfirmProvider>
          <ToastProvider>
          {/* Two full-screen blocks that sit above everything, login included:
              the retiring iPhone Home Screen web app (install from the App
              Store instead), and a store build below the server's minimum
              version (update from the App Store). Both render nothing
              anywhere else. */}
          <MoveToAppStoreGate />
          <NativeUpdateGate />
          <UpdatePrompt />
          {/* Native-app device lock. Renders nothing on the web, when nobody
              is signed in, or when the user has not switched it on. It covers
              the UI rather than replacing it, so unlocking is instant and no
              in-flight query is lost. */}
          {/* Store app only: a push landing while the app is open refreshes
              the bell, a tap on one opens what it is about, and a link to
              www.phiegcrm.com opened from another app lands on its page.
              Nothing on the web (the service worker does the push part). */}
          <NativeEvents />
          {/* A lazy route whose chunk no longer exists (tab left open across a
              deploy) used to throw with nothing to catch it, leaving a blank
              page. The boundary reloads once to pick up the new build. */}
          <ChunkErrorBoundary>
          <Suspense
            fallback={<LoadingGate loading fullscreen minHeight={360} />}
          >
          <Routes>
            {/* Notification-preference entry point (from lead emails).
                Mutates nothing — routes to authenticated Settings.
                /unsubscribe/:clientId kept so old email links land safely. */}
            <Route
              path="/notification-preferences"
              element={<Unsubscribe />}
            />
            <Route path="/unsubscribe/:clientId" element={<Unsubscribe />} />
            {/* Newsletter opt-out (public, from the email footer). Opening
                it changes nothing; the button click does. */}
            <Route
              path="/newsletter/unsubscribe"
              element={<NewsletterUnsubscribe />}
            />
            {/* Public install page — reachable logged-out so a brand-new client
                can add the app to their home screen from a shared link/QR. */}
            <Route path="/get-app" element={<GetApp />} />
            {/* Shorter, easier-to-share aliases for the install page. */}
            <Route path="/app" element={<GetApp />} />
            <Route path="/install" element={<GetApp />} />
            {/* Forgot password. Public and listed BEFORE the signed-out
                catch-all below, which would otherwise swallow /reset-password
                and bounce someone arriving from the email straight to /login
                with their one-time link thrown away. */}
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            {/* Redirect to appropriate route based on user */}
            {user ? (
              <Route
                path="/login"
                element={
                  <Navigate
                    to={gatedToSetPassword ? "/set-password" : "/dashboard"}
                  />
                }
              />
            ) : (
              <Route path="/login" element={<Login />} />
            )}

            {user ? (
              <Route
                path="/"
                element={
                  <Navigate
                    to={gatedToSetPassword ? "/set-password" : "/dashboard"}
                  />
                }
              />
            ) : (
              <Route path="/*" element={<Navigate to="/login" />} />
            )}

            {/* Bootstrap accounts (admin-created, no password yet) are pinned
                to this screen until they pick a real password. All role
                blocks below are gated off so no business route renders. */}
            {user && gatedToSetPassword && (
              <Route path="/set-password" element={<SetInitialPassword />} />
            )}
            {user && gatedToSetPassword && (
              <Route
                path="/*"
                element={<Navigate to="/set-password" replace />}
              />
            )}

            {/* Admin Routes */}
            {user && !gatedToSetPassword && isAdmin(user) && (
              <Route element={<Layout />}>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<Dashboard />} />

                <Route path="/prefixes" element={<Projects />} />
                <Route path="/prefixes/create" element={<CreateProject />} />
                <Route path="/prefixes/:id" element={<SingleProject />} />
                <Route path="/prefixes/update/:id" element={<UpdateProject />} />
                {/* Legacy /projects/* → /prefixes/* */}
                <Route path="/projects/*" element={<LegacyProjectsRedirect />} />

                <Route path="/clients" element={<Clients />} />
                <Route path="/clients/create" element={<CreateClient />} />
                <Route path="/clients/:id" element={<SingleClient />} />
                <Route path="/clients/update/:id" element={<UpdateClient />} />

                <Route path="/leads" element={<Leads />} />
                <Route path="/partner-leads" element={<Leads scope="partners" />} />
                <Route path="/leads/:id" element={<SingleLead />} />
                <Route path="/sync-history" element={<SyncHistory />} />

                <Route path="/team" element={<Team />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/notifications" element={<Notifications />} />
                <Route path="/crm-delivery" element={<CrmDelivery />} />
                <Route path="/broadcast" element={<Broadcast />} />

                {/* Media Buying module — Admins get the full module. Sections
                    are sidebar nav items under /media-buying/:section. */}
                <Route
                  path="/media-buying"
                  element={<Navigate to="/media-buying/overview" replace />}
                />
                <Route path="/media-buying/new-client" element={<NewClientHome />} />
                <Route path="/media-buying/:section" element={<MediaBuyingHome />} />

                {/* Financial Manager pages — every Admin gets these routes.
                    The same paths are also rendered by the Financial Manager
                    role block below (which is the only thing that role sees). */}
                {canViewFinancialDashboard(user) && (
                  <Route
                    path="/financialdashboard"
                    element={<FinancialDashboard />}
                  />
                )}
                {canViewFinancialDashboard(user) && (
                  <Route
                    path="/unmatchedcampaigns"
                    element={<UnmatchedCampaigns />}
                  />
                )}
                {canViewFinancialDashboard(user) && (
                  <Route path="/clientsettings" element={<ClientPricing />} />
                )}
                {canViewFinancialDashboard(user) && (
                  <Route
                    path="/clientpricing"
                    element={<Navigate to="/clientsettings" replace />}
                  />
                )}
                {canViewFinancialDashboard(user) && (
                  <Route path="/mappings" element={<Mappings />} />
                )}
                {canViewFinancialDashboard(user) && (
                  <Route path="/partners" element={<Partners />} />
                )}
                {canViewFinancialDashboard(user) && (
                  <Route path="/invoices" element={<Invoices />} />
                )}
                {canViewFinancialDashboard(user) && (
                  <Route path="/budgettracker" element={<BudgetTracker />} />
                )}
                {canViewFinancialDashboard(user) && (
                  <Route path="/activityaudit" element={<ActivityAudit />} />
                )}
                {canViewFinancialDashboard(user) && (
                  <Route
                    path="/financialclient/:clientId"
                    element={<ClientFinancialDetail />}
                  />
                )}
              </Route>
            )}

            {/* Financial Manager Routes — sees ONLY the financial dashboard.
                Anything else is bounced back to /financialdashboard. */}
            {user && !gatedToSetPassword && isFinancialManager(user) && (
              <Route element={<Layout />}>
                <Route
                  path="/"
                  element={<Navigate to="/financialdashboard" replace />}
                />
                <Route
                  path="/dashboard"
                  element={<Navigate to="/financialdashboard" replace />}
                />
                <Route
                  path="/financialdashboard"
                  element={<FinancialDashboard />}
                />
                <Route
                  path="/unmatchedcampaigns"
                  element={<UnmatchedCampaigns />}
                />
                <Route path="/clientsettings" element={<ClientPricing />} />
                <Route
                  path="/clientpricing"
                  element={<Navigate to="/clientsettings" replace />}
                />
                <Route path="/mappings" element={<Mappings />} />
                <Route path="/partners" element={<Partners />} />
                <Route path="/invoices" element={<Invoices />} />
                <Route path="/budgettracker" element={<BudgetTracker />} />
                {/* FM-only Clients tab: names + activate/deactivate, nothing
                    else. Admins keep the full /clients page instead. */}
                <Route path="/clients" element={<FMClients />} />
                <Route path="/notifications" element={<Notifications />} />
                {/* Self-service settings (push, passkeys, password) — the
                    Navbar gear and the push-setup banner both point here. */}
                <Route path="/settings" element={<Settings />} />
                <Route path="/activityaudit" element={<ActivityAudit />} />
                <Route
                  path="/financialclient/:clientId"
                  element={<ClientFinancialDetail />}
                />
                <Route
                  path="/*"
                  element={<NotFound homePath="/financialdashboard" />}
                />
              </Route>
            )}

            {/* Media Buyer AND Media Buying Team Leader Routes — both sealed to
                the Media Buying module only, and both scoped by the backend:
                a buyer to their own assignments, a leader to their squad's
                (lib/mediaBuying/scope.js). Identical route shape, so one
                branch serves both; which TABS a leader sees is decided by the
                nav and their permission map, not here. */}
            {user &&
              !gatedToSetPassword &&
              (isMediaBuyer(user) || isMediaBuyingTeamLeader(user)) && (
              <Route element={<Layout />}>
                <Route
                  path="/"
                  element={<Navigate to="/media-buying" replace />}
                />
                <Route
                  path="/dashboard"
                  element={<Navigate to="/media-buying" replace />}
                />
                <Route
                  path="/media-buying"
                  element={<Navigate to="/media-buying/overview" replace />}
                />
                <Route path="/media-buying/new-client" element={<NewClientHome />} />
                <Route path="/media-buying/:section" element={<MediaBuyingHome />} />
                <Route path="/notifications" element={<Notifications />} />
                {/* Self-service settings (push, passkeys, password) — the
                    bottom-nav Settings tab, the Navbar gear and the
                    push-setup banner all point here. */}
                <Route path="/settings" element={<Settings />} />
                <Route
                  path="/*"
                  element={<NotFound homePath="/media-buying" />}
                />
              </Route>
            )}

            {/* Media Buying Head Routes — full Media Buying oversight plus the
                Budget Tracker as their single financial-related surface. */}
            {user && !gatedToSetPassword && isMediaBuyingHead(user) && (
              <Route element={<Layout />}>
                <Route
                  path="/"
                  element={<Navigate to="/media-buying" replace />}
                />
                <Route
                  path="/dashboard"
                  element={<Navigate to="/media-buying" replace />}
                />
                <Route
                  path="/media-buying"
                  element={<Navigate to="/media-buying/overview" replace />}
                />
                <Route path="/media-buying/new-client" element={<NewClientHome />} />
                <Route path="/media-buying/:section" element={<MediaBuyingHome />} />
                {/* The lead desk. The Head sees the leads their campaigns
                    produced and can put an unassigned one on the right client;
                    the API allow-list (mediaBuyingHeadCrmAccess) is what
                    actually bounds this — these routes only decide what renders. */}
                <Route path="/leads" element={<Leads />} />
                <Route path="/partner-leads" element={<Leads scope="partners" />} />
                <Route path="/leads/:id" element={<SingleLead />} />
                <Route path="/clients" element={<Clients />} />
                <Route path="/clients/:id" element={<SingleClient />} />
                <Route path="/notifications" element={<Notifications />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/budgettracker" element={<BudgetTracker />} />
                <Route path="/headfinancials" element={<HeadFinancials />} />
                <Route
                  path="/*"
                  element={<NotFound homePath="/media-buying" />}
                />
              </Route>
            )}

            {/* Account Manager Routes */}
            {user && !gatedToSetPassword && isAccountManager(user) && (
              <Route element={<Layout />}>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<AccountManagerDashboard />} />

                {/* Managed-clients pages (view + create; the created client
                    is auto-assigned to this AM server-side) */}
                <Route path="/clients" element={<Clients />} />
                <Route path="/clients/create" element={<CreateClient />} />
                <Route path="/clients/:id" element={<SingleClient />} />
                <Route path="/leads" element={<Leads />} />
                <Route path="/leads/:id" element={<SingleLead />} />

                {/* Personal lead book */}
                <Route path="/myleads" element={<MyLeads />} />
                <Route path="/myleads/:id" element={<SingleMyLead />} />
                <Route path="/am-projects" element={<AmProjects />} />
                <Route path="/notifications" element={<Notifications />} />

                {/* Market-update broadcasts to the AM's managed clients
                    (scope enforced server-side per request) */}
                <Route path="/broadcast" element={<Broadcast />} />

                <Route path="/settings" element={<Settings />} />

                {/* Anything else (e.g., /projects*) sends the AM home */}
                <Route path="/*" element={<NotFound homePath="/dashboard" />} />
              </Route>
            )}

            {/* Client Routes */}
            {user && !gatedToSetPassword && user.role === "Client" && (
              <Route element={<Layout />}>
                {/* Client Dashboard */}
                <Route
                  path="/"
                  element={<Navigate to="/clientdashboard" replace />}
                />
                <Route
                  path="/dashboard"
                  element={<Navigate to="/clientdashboard" replace />}
                />
                <Route path="/clientdashboard" element={<ClientDashboard />} />

                {/* Client Leads */}
                <Route
                  path="/leads"
                  element={<RedirectWithSearch to="/clientleads" />}
                />
                <Route path="/clientleads" element={<ClientLeads />} />
                <Route path="/leads/:id" element={<SingleLead />} />

                {/* Handle Other Paths */}
                <Route
                  path="/*"
                  element={<NotFound homePath="/clientdashboard" />}
                />

                {/* Client Settings */}
                <Route
                  path="/settings"
                  element={<Navigate to="/clientsettings" replace />}
                />
                <Route path="/clientsettings" element={<ClientSettings />} />
                <Route path="/notifications" element={<Notifications />} />

                {/* Client Team Members */}
                <Route path="/teammembers" element={<ClientTeamMembers />} />
                <Route path="/teammembers/:id" element={<SingleTeamMember />} />
                <Route
                  path="/teammembers/create"
                  element={<CreateTeamMember />}
                />
              </Route>
            )}

            {/* Team Member Routes */}
            {user && !gatedToSetPassword && user.role === "Team Member" && (
              <Route element={<Layout />}>
                {/* Client Dashboard */}
                <Route
                  path="/"
                  element={<Navigate to="/teammemberdashboard" replace />}
                />
                <Route
                  path="/dashboard"
                  element={<Navigate to="/teammemberdashboard" replace />}
                />
                <Route
                  path="/teammemberdashboard"
                  element={<TeamMemberDashboard />}
                />

                {/* Client Leads */}
                <Route
                  path="/leads"
                  element={<RedirectWithSearch to="/teammemberleads" />}
                />
                <Route path="/teammemberleads" element={<TeamMemberLeads />} />
                <Route path="/leads/:id" element={<SingleLead />} />

                {/* Team Member Settings */}
                <Route
                  path="/settings"
                  element={<Navigate to="/teammembersettings" replace />}
                />
                <Route
                  path="/teammembersettings"
                  element={<TeamMemberSettings />}
                />
                <Route path="/notifications" element={<Notifications />} />

                {/* Handle Other Paths */}
                <Route
                  path="/*"
                  element={<NotFound homePath="/teammemberdashboard" />}
                />
              </Route>
            )}

            {/* Super Client Routes */}
            {user && !gatedToSetPassword && user.role === "Super Client" && (
              <Route element={<Layout />}>
                {/* Super Client Dashboard */}
                <Route
                  path="/"
                  element={<Navigate to="/superclientdashboard" replace />}
                />
                <Route
                  path="/dashboard"
                  element={<Navigate to="/superclientdashboard" replace />}
                />
                <Route
                  path="/superclientdashboard"
                  element={<SuperClientDashboard />}
                />

                {/* Super Client Leads */}
                <Route
                  path="/leads"
                  element={<RedirectWithSearch to="/superclientleads" />}
                />
                <Route
                  path="/superclientleads"
                  element={<SuperClientLeads />}
                />
                <Route path="/leads/:id" element={<SingleLead />} />

                {/* Super Client Settings */}
                <Route
                  path="/settings"
                  element={<Navigate to="/superclientsettings" replace />}
                />
                <Route
                  path="/superclientsettings"
                  element={<SuperClientSettings />}
                />
                <Route path="/notifications" element={<Notifications />} />

                {/* Handle Other Paths */}
                <Route
                  path="/*"
                  element={<NotFound homePath="/superclientdashboard" />}
                />
              </Route>
            )}
          </Routes>
          </Suspense>
          </ChunkErrorBoundary>
          </ToastProvider>
          </ConfirmProvider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
